import { useState } from 'react';

import type { KeyboardEvent } from 'react';
import type { PlanningEntityType } from 'generated/index';

import {
  getInitialActiveFilter,
  getPickerTypeByFilterCommand,
  getVisibleTypeFilters
} from '../PlanningEntityPicker.utils';

import type { PlanningEntityTypeFilterOption } from '../PlanningEntityPicker.types';

type UsePlanningEntityTypeFilterParams = {
  entityTypes: PlanningEntityType[];
  searchOrganizationalUnits: boolean;
  typeFilters?: readonly PlanningEntityTypeFilterOption[];
  defaultTypeFilter?: PlanningEntityTypeFilterOption;
};

/**
 * The type chips: which are shown, which are on, and the typed shortcuts ("/" then a letter) that switch to
 * one type, as in EntitySearch. A chip toggles; "Alle" switches them all off. Without an Alle chip the last
 * chip that is on stays on, since none on would mean all.
 */
export const usePlanningEntityTypeFilter = ({
  entityTypes,
  searchOrganizationalUnits,
  typeFilters,
  defaultTypeFilter
}: UsePlanningEntityTypeFilterParams) => {
  'use memo';

  const visible = getVisibleTypeFilters(
    typeFilters,
    entityTypes,
    searchOrganizationalUnits
  );
  const { showAll, types } = visible;
  const [activeFilter, setActiveFilter] = useState<PlanningEntityType[]>(() =>
    getInitialActiveFilter(defaultTypeFilter, visible)
  );

  const selectType = (type: PlanningEntityType | null) => {
    if (!type) {
      if (showAll) setActiveFilter([]);
      return;
    }
    if (!types.includes(type)) return;
    setActiveFilter((current) => {
      if (!current.includes(type)) return [...current, type];
      const next = current.filter((active) => active !== type);
      return next.length === 0 && !showAll ? current : next;
    });
  };

  /** Applies a typed shortcut; true when the key press was one, so the caller clears the "/". */
  const applyFilterCommand = (
    event: KeyboardEvent<HTMLElement>,
    query: string
  ) => {
    if (query !== '/' && query !== '\\') return false;

    const type = getPickerTypeByFilterCommand(event, searchOrganizationalUnits);
    // Shift is let through for capitals; an unknown letter, or a chip that is not shown, is typed as it is.
    if (type === undefined || type === 'modifier') return false;
    if (type === null ? !showAll : !types.includes(type)) return false;

    event.preventDefault();
    setActiveFilter(type ? [type] : []);
    return true;
  };

  return { showAll, types, activeFilter, selectType, applyFilterCommand };
};
